import { useEffect, useMemo, useState } from 'react';
import { type DocumentDto, type DocumentKind } from '@shared';
import { apiGet, ApiError } from '../../lib/api';

// KYC is intentionally NOT a documents category here — it is managed entirely
// on /portal/compliance. This page lists only client-shared deliverables and
// reports; any `kind:'kyc'` row is filtered out (see `grouped` below).
type ClientDocKind = Exclude<DocumentKind, 'kyc'>;

function formatSize(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Small inline icon for the download button — keeps the brand minimal.
function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M2.5 13.5h11" />
    </svg>
  );
}

const KIND_LABEL: Record<ClientDocKind, string> = {
  deliverable: 'Deliverable',
  final_report: 'Final report',
  engagement_letter: 'Engagement letter',
  other: 'Other',
};

const KIND_TONE: Record<ClientDocKind, string> = {
  deliverable: 'accent',
  final_report: 'green',
  engagement_letter: 'neutral',
  other: 'neutral',
};

export function PortalDocuments() {
  const [rows, setRows] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ documents: DocumentDto[] }>('/api/documents')
      .then((r) => setRows(r.documents))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  // GET /api/documents/:id/download mints a fresh 5-minute SAS URL each click.
  // We open in a new tab so the portal stays put + the browser handles the
  // file's Save-As dialog naturally.
  async function handleDownload(d: DocumentDto) {
    setDownloading(d.id);
    setError(null);
    try {
      const r = await apiGet<{ url: string }>(`/api/documents/${d.id}/download`);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Download failed.');
    } finally {
      setDownloading(null);
    }
  }

  const grouped = useMemo(() => {
    const m: Record<ClientDocKind, DocumentDto[]> = {
      deliverable: [], final_report: [], engagement_letter: [], other: [],
    };
    for (const d of rows ?? []) {
      // KYC documents belong to /portal/compliance, never the documents list.
      if (d.kind === 'kyc') continue;
      m[d.kind as ClientDocKind].push(d);
    }
    return m;
  }, [rows]);

  // Whether any non-KYC document exists to show (drives the empty state without
  // tripping on a client whose only rows are KYC-kind).
  const hasDocuments = useMemo(
    () => (Object.keys(grouped) as ClientDocKind[]).some((k) => grouped[k].length > 0),
    [grouped],
  );

  return (
    <>
      <div>
        <h1 className="portal-h1">Documents</h1>
        <p className="portal-sub">
          Engagement letters, deliverables and final reports shared with you by Omega.
          Each document is dual-signed by your engagement lead and Compliance before being released here.
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {rows !== null && !hasDocuments && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--fg-mute)' }}>
          No documents shared with you yet. Your engagement lead will publish here as deliverables land.
        </div>
      )}

      {rows !== null && hasDocuments && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 20 }}>
          {(Object.keys(grouped) as ClientDocKind[]).map((k) =>
            grouped[k].length > 0 && (
              <section key={k}>
                <h3 style={{ fontSize: 14, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  {KIND_LABEL[k]}
                </h3>
                <div className="proposal-grid">
                  {grouped[k].map((d) => (
                    <article key={d.id} className="card proposal-card">
                      <header className="proposal-hd">
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14.5, lineHeight: 1.4 }}>{d.title}</div>
                          <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                            {d.filename}
                          </div>
                        </div>
                        <span className={`chip ${KIND_TONE[k]}`}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                          {KIND_LABEL[k]}
                        </span>
                      </header>
                      <div className="proposal-meta">
                        <span>Size</span>
                        <strong>{formatSize(d.size_bytes)}</strong>
                      </div>
                      <div className="proposal-meta">
                        <span>Published</span>
                        <strong>{d.updated_at.slice(0, 10)}</strong>
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          className="btn sm"
                          disabled={downloading === d.id}
                          onClick={() => handleDownload(d)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <DownloadIcon />
                          {downloading === d.id ? 'Opening…' : 'Download'}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )
          )}
        </div>
      )}
    </>
  );
}
